/**
 * Phase 6.4 — After-hours confirmation + escalation + downstream clamp.
 *
 * Doctrine:
 *   - Bucket 5 §5.3.8  After-hours active is a DISTINCT availability state.
 *   - Bucket 5 §5.4.x  After-hours running timer must be CONFIRMED, not
 *                      auto-stopped and not silently rewritten.
 *   - Bucket 5 §5.7.x  Schedule clamp must use the next valid work
 *                      window. No fake overnight availability.
 *   - Bucket 4.5 §4.5.5  Due Out outranks Waiter Flex; physical
 *                        constraints outrank Due Out.
 *   - Bucket 4.5 §4.5.9  Waiter Flex MUST NOT extend Due Out.
 *
 * Why this module exists
 * ----------------------
 * The existing `src/utils/afterHoursDetection.ts:isAfterHours` returns
 * a boolean. Phase 1.2's `getTechnicianAvailabilityState` already
 * recognises `after_hours_active` as one of its 12 states. Neither
 * provides:
 *   - A confirmation-required signal when a timer is running past shift.
 *   - A grace window before escalation.
 *   - A manager / service-writer warning emission.
 *   - A downstream cascade clamp to the next valid work window.
 *
 * Bucket 5 doctrine requires all four. Phase 6.4 supplies the canonical
 * vocabulary + math helpers so the next round can wire one or two of
 * the safest adoption points (display banner, cascade clamp) without
 * rewriting timers / cascade / AutoBump.
 *
 * Phase 6.4 scope: HELPER + TESTS ONLY.
 *   - Pure. No DB I/O, no clock reads, no React hooks. Caller passes
 *     `now`, shift/shop windows, and the confirmation state.
 *   - No DB migration. The future `after_hours_confirmed` flag (or
 *     event row) lands when adoption begins.
 *   - No runtime wiring. The timer state machine, cascade, AutoBump,
 *     OptiAssign, pull-forward, and partial-day absence paths are
 *     untouched.
 *
 * Helper invariants (pinned by tests):
 *   - The timer action union NEVER contains `auto_stop`.
 *   - The verdict NEVER exposes a `startTimeOverride` field. Actual
 *     start time is historical truth and is NOT modified.
 *   - `clampFlexibleStartToNextWorkWindow` clamps ONLY when after-
 *     hours scheduling is NOT confirmed.
 *
 * Builds on:
 *   - Phase 1.2 `getTechnicianAvailabilityState` (recognises
 *     `after_hours_active`; once Phase 6.4 wires confirmation, the
 *     same state can be subdivided into the confirmation sub-states
 *     below).
 *   - Phase 6.3 `partialDayAbsence` (absence intervals and after-hours
 *     are orthogonal; the verdict surfaces appropriately for each).
 */

// =============================================================================
// State + action vocabulary
// =============================================================================

/**
 * Discrete after-hours work states per Bucket 5 §5.3.8 + §5.4.x.
 *
 *   - 'within_hours'              — current time is within shop /
 *                                   technician shift window.
 *   - 'after_hours_unconfirmed'   — current time is past shift end AND
 *                                   timer is running AND confirmation
 *                                   has not been requested or just
 *                                   started (grace clock starts here).
 *   - 'after_hours_grace'         — confirmation pending within the
 *                                   grace window. Warn, do not escalate.
 *   - 'after_hours_escalated'     — grace window elapsed without
 *                                   confirmation. Emit manager / writer
 *                                   warning.
 *   - 'after_hours_confirmed'     — user confirmed the after-hours work.
 *                                   Timer continues legitimately.
 *   - 'off_shift_not_running'     — past shift end with NO running
 *                                   timer; no after-hours signal at all.
 */
export type AfterHoursWorkState =
  | 'within_hours'
  | 'after_hours_unconfirmed'
  | 'after_hours_grace'
  | 'after_hours_escalated'
  | 'after_hours_confirmed'
  | 'off_shift_not_running';

export const AFTER_HOURS_WORK_STATES = Object.freeze([
  'within_hours',
  'after_hours_unconfirmed',
  'after_hours_grace',
  'after_hours_escalated',
  'after_hours_confirmed',
  'off_shift_not_running',
] as const) satisfies ReadonlyArray<AfterHoursWorkState>;

/**
 * Discrete timer-action recommendations. The helper's contract is
 * explicit: NO `auto_stop`, NO `rewrite_start_time`. The action set
 * only contains recommendations the system surfaces to a human, never
 * one that silently mutates timer/start-time state.
 */
export type AfterHoursTimerAction =
  | 'none'                  // no timer / no signal
  | 'continue'              // confirmed after-hours; let it run
  | 'request_confirmation'  // ask "Working After Hours?"
  | 'warn_grace'            // confirmation pending within grace
  | 'escalate';             // grace elapsed; manager warning

export const AFTER_HOURS_TIMER_ACTIONS = Object.freeze([
  'none',
  'continue',
  'request_confirmation',
  'warn_grace',
  'escalate',
] as const) satisfies ReadonlyArray<AfterHoursTimerAction>;

/**
 * Discrete blocker codes for the verdict's `blockers[]` list.
 * Machine-readable so UI / cascade / audit-log can route each blocker
 * without parsing strings.
 */
export type AfterHoursBlocker =
  | 'confirmation_required'
  | 'grace_elapsed'
  | 'manager_warning_required'
  | 'downstream_clamp_required'
  | 'no_valid_work_window'
  | 'confirmed_after_hours'
  | 'not_running'
  | 'missing_shift_window'
  | 'missing_shop_hours'
  | 'invalid_time_input';

// =============================================================================
// Input shapes
// =============================================================================

/**
 * Caller-supplied context for the after-hours work verdict.
 *
 * `now` is required. `shiftEnd` is required for the "is past shift?"
 * check; without it the verdict short-circuits with
 * `missing_shift_window`. `shopClose` is optional — provides a
 * facility-level second check when tech-shift data is absent.
 *
 * `unconfirmedSinceAt` is the moment confirmation first became
 * required. Typically `unconfirmedSinceAt === shiftEnd` (running timer
 * crossed into after-hours at shift end). Callers that track the
 * "ask appeared at" timestamp separately can pass it explicitly.
 */
export interface AfterHoursContext {
  now: Date;
  shiftEnd?: Date | string | null;
  shopClose?: Date | string | null;
  /** True when at least one timer is currently running. */
  isTimerRunning?: boolean;
  /**
   * `true` when the user has explicitly confirmed working after hours
   * for this session. The helper never auto-confirms — callers set
   * this from an explicit user action (Phase 6.5+ UI).
   */
  afterHoursConfirmed?: boolean;
  /**
   * When confirmation first became required. Defaults to `shiftEnd`
   * when omitted.
   */
  unconfirmedSinceAt?: Date | string | null;
  /**
   * Grace minutes before escalation. Defaults to 15 minutes. Shops
   * may override via shop culture (Phase 6.5+ adoption).
   */
  graceMin?: number;
}

/**
 * The verdict structure. The `startTimePreserved: true` literal type
 * pins the doctrinal claim that this helper NEVER returns a value
 * that could rewrite start_time — actual start time stays historical
 * truth.
 */
export interface AfterHoursWorkVerdict {
  state: AfterHoursWorkState;
  timerAction: AfterHoursTimerAction;
  blockers: AfterHoursBlocker[];
  /**
   * Pinned literal — proves at the type level that the helper never
   * yields a start-time override.
   */
  startTimePreserved: true;
  /**
   * Elapsed minutes since `unconfirmedSinceAt`. `null` when not
   * applicable (no timer, within hours, no shift end, etc.).
   */
  unconfirmedMinutes: number | null;
  /** Effective grace minutes used in the verdict. */
  effectiveGraceMin: number;
}

// =============================================================================
// Internal helpers
// =============================================================================

const DEFAULT_GRACE_MIN = 15;

function parseTs(v: Date | string | null | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

function parseHHmm(s: string | null | undefined): { hours: number; minutes: number } | null {
  if (!s) return null;
  const [h, m] = String(s).split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return { hours: h, minutes: m };
}

function clampGrace(graceMin: number | undefined): number {
  if (!Number.isFinite(graceMin as number)) return DEFAULT_GRACE_MIN;
  if ((graceMin as number) < 0) return 0;
  return graceMin as number;
}

// =============================================================================
// Public API — core verdict
// =============================================================================

/**
 * Compute the after-hours work verdict.
 *
 * Resolution order:
 *   1. `shiftEnd` missing → `state='within_hours'` defensively with
 *      `missing_shift_window` blocker. (Conservative: don't escalate
 *      without ground truth.)
 *   2. `now < shiftEnd` → 'within_hours' / no action.
 *   3. `now >= shiftEnd` AND timer NOT running → 'off_shift_not_running'
 *      / no action.
 *   4. `now >= shiftEnd` AND timer running:
 *        a. `afterHoursConfirmed === true` → 'after_hours_confirmed' /
 *           'continue' (no blockers beyond the diagnostic).
 *        b. Compute `unconfirmedMinutes = (now - unconfirmedSinceAt) /
 *           60_000`. `unconfirmedSinceAt` defaults to `shiftEnd`.
 *        c. `unconfirmedMinutes <= 0` → 'after_hours_unconfirmed' /
 *           'request_confirmation' / [confirmation_required].
 *        d. `unconfirmedMinutes <= graceMin` → 'after_hours_grace' /
 *           'warn_grace' / [confirmation_required].
 *        e. `unconfirmedMinutes > graceMin` → 'after_hours_escalated' /
 *           'escalate' / [confirmation_required, grace_elapsed,
 *           manager_warning_required].
 *
 * Pure: no I/O, no clock reads, no DB.
 */
export function getAfterHoursWorkState(
  context: AfterHoursContext,
): AfterHoursWorkVerdict {
  const blockers: AfterHoursBlocker[] = [];
  const effectiveGraceMin = clampGrace(context.graceMin);
  const nowMs = context.now?.getTime?.();

  if (!Number.isFinite(nowMs as number)) {
    blockers.push('invalid_time_input');
    return {
      state: 'within_hours',
      timerAction: 'none',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: null,
      effectiveGraceMin,
    };
  }

  const shiftEndMs = parseTs(context.shiftEnd);
  if (shiftEndMs == null) {
    blockers.push('missing_shift_window');
    return {
      state: 'within_hours',
      timerAction: 'none',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: null,
      effectiveGraceMin,
    };
  }

  // Within shift?
  if ((nowMs as number) < shiftEndMs) {
    return {
      state: 'within_hours',
      timerAction: 'none',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: null,
      effectiveGraceMin,
    };
  }

  // Past shift; no timer running.
  if (context.isTimerRunning !== true) {
    blockers.push('not_running');
    return {
      state: 'off_shift_not_running',
      timerAction: 'none',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: null,
      effectiveGraceMin,
    };
  }

  // Past shift; timer running; confirmed.
  if (context.afterHoursConfirmed === true) {
    blockers.push('confirmed_after_hours');
    return {
      state: 'after_hours_confirmed',
      timerAction: 'continue',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: null,
      effectiveGraceMin,
    };
  }

  // Past shift; timer running; NOT confirmed.
  const sinceMs = parseTs(context.unconfirmedSinceAt) ?? shiftEndMs;
  const unconfirmedMin = ((nowMs as number) - sinceMs) / 60_000;

  if (unconfirmedMin <= 0) {
    blockers.push('confirmation_required');
    return {
      state: 'after_hours_unconfirmed',
      timerAction: 'request_confirmation',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: 0,
      effectiveGraceMin,
    };
  }

  if (unconfirmedMin <= effectiveGraceMin) {
    blockers.push('confirmation_required');
    return {
      state: 'after_hours_grace',
      timerAction: 'warn_grace',
      blockers,
      startTimePreserved: true,
      unconfirmedMinutes: unconfirmedMin,
      effectiveGraceMin,
    };
  }

  blockers.push('confirmation_required');
  blockers.push('grace_elapsed');
  blockers.push('manager_warning_required');
  return {
    state: 'after_hours_escalated',
    timerAction: 'escalate',
    blockers,
    startTimePreserved: true,
    unconfirmedMinutes: unconfirmedMin,
    effectiveGraceMin,
  };
}

// =============================================================================
// Public API — boolean shortcuts
// =============================================================================

/** True iff a timer is running past `shiftEnd`. */
export function isTimerRunningAfterHours(context: AfterHoursContext): boolean {
  if (context.isTimerRunning !== true) return false;
  const shiftEndMs = parseTs(context.shiftEnd);
  const nowMs = context.now?.getTime?.();
  if (shiftEndMs == null || !Number.isFinite(nowMs as number)) return false;
  return (nowMs as number) >= shiftEndMs;
}

/** True iff the verdict says confirmation is required. */
export function requiresAfterHoursConfirmation(context: AfterHoursContext): boolean {
  const v = getAfterHoursWorkState(context);
  return (
    v.state === 'after_hours_unconfirmed' ||
    v.state === 'after_hours_grace' ||
    v.state === 'after_hours_escalated'
  );
}

/** True iff the verdict is in the escalated state. */
export function shouldEscalateUnconfirmedAfterHours(
  context: AfterHoursContext,
): boolean {
  return getAfterHoursWorkState(context).state === 'after_hours_escalated';
}

/** Convenience: return the verdict's state directly. */
export function getAfterHoursEscalationState(
  context: AfterHoursContext,
): AfterHoursWorkState {
  return getAfterHoursWorkState(context).state;
}

/** Convenience: return the verdict's blocker list directly. */
export function getAfterHoursBlockers(
  context: AfterHoursContext,
): AfterHoursBlocker[] {
  return getAfterHoursWorkState(context).blockers;
}

// =============================================================================
// Public API — next valid work window + clamp
// =============================================================================

/** Map of day-of-week → optional `{ open, close }` "HH:mm" pair. */
export type ShopHoursByDay = Record<
  number | string,
  { open?: string | null; close?: string | null } | null | undefined
>;

export interface NextValidWindowInput {
  now: Date;
  shopHoursByDay?: ShopHoursByDay | null;
  /** Look-ahead horizon in days. Default 7. Bound to [1, 31]. */
  maxDaysAhead?: number;
}

/**
 * Find the next valid work-window start for the given `now` and shop
 * hours. Returns:
 *   - `null` when no valid window exists within the horizon.
 *   - A `Date` instant when the next open window is found. If `now`
 *     is already inside today's open window, returns `now`. Otherwise
 *     walks forward day-by-day, skipping closed days.
 *
 * Pure. Caller supplies the shop-hours map (typically loaded from
 * `shop_profile.hours`). The helper does not query the DB.
 */
export function getNextValidWorkWindowStart(
  input: NextValidWindowInput,
): Date | null {
  const { now, shopHoursByDay } = input;
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
  if (!shopHoursByDay) return null;

  const horizon = Math.max(
    1,
    Math.min(31, Math.floor(input.maxDaysAhead ?? 7)),
  );

  // Day 0 = today; check if `now` fits within today's window. If so,
  // return `now` directly (already in valid window).
  for (let dayOffset = 0; dayOffset <= horizon; dayOffset++) {
    const candidate = new Date(now.getTime());
    candidate.setDate(candidate.getDate() + dayOffset);
    const dow = candidate.getDay();
    const dayEntry =
      (shopHoursByDay as any)[dow] ?? (shopHoursByDay as any)[String(dow)];
    if (!dayEntry) continue;
    const open = parseHHmm(dayEntry.open);
    const close = parseHHmm(dayEntry.close);
    if (!open || !close) continue;

    // Build today's open/close instants.
    const openAt = new Date(candidate.getTime());
    openAt.setHours(open.hours, open.minutes, 0, 0);
    const closeAt = new Date(candidate.getTime());
    closeAt.setHours(close.hours, close.minutes, 0, 0);

    if (dayOffset === 0) {
      // Today: if `now` < open → use open; if open <= now < close →
      // use now; if now >= close → skip to next day.
      if (now.getTime() < openAt.getTime()) return openAt;
      if (now.getTime() < closeAt.getTime()) return now;
      // else: past close; fall through to next day.
      continue;
    }

    return openAt;
  }

  return null;
}

export interface ClampFlexibleStartInput {
  /** Proposed start time. */
  proposedStart: Date | string;
  /** Reference clock. */
  now: Date;
  /** Tech's shift end for the proposed-start day (HH:mm or Date). */
  shiftEnd?: Date | string | null;
  /** Shop close time for the proposed-start day. */
  shopClose?: Date | string | null;
  /** Shop hours by day-of-week (for next-window lookup). */
  shopHoursByDay?: ShopHoursByDay | null;
  /**
   * Is this ticket FLEXIBLE (drop-off without locked start)? Locked
   * tickets (waiters with promise, pinned, comeback continuity) are
   * never clamped — caller declares this.
   */
  isFlexible?: boolean;
  /**
   * Has the user confirmed after-hours scheduling for this ticket?
   * Default `false`. When `true`, no clamp is applied even for
   * flexible tickets.
   */
  afterHoursConfirmed?: boolean;
}

export interface ClampFlexibleStartResult {
  /** True iff the helper recommends clamping the start. */
  shouldClamp: boolean;
  /** When `shouldClamp` is true: the clamped start time. */
  clampedStart: Date | null;
  /** Blockers / reasons. */
  blockers: AfterHoursBlocker[];
}

/**
 * Decide whether a flexible ticket's proposed start time should be
 * clamped to the next valid work window. Returns the clamped value
 * when applicable; otherwise leaves the proposed start unchanged.
 *
 * Resolution:
 *   - Invalid `proposedStart` → blockers=['invalid_time_input'],
 *     no clamp.
 *   - Not flexible → no clamp (caller declared the ticket immutable).
 *   - After-hours scheduling explicitly confirmed → no clamp.
 *   - Proposed-start is WITHIN shift end / shop close → no clamp.
 *   - Else: compute next valid work window from `shopHoursByDay`;
 *     when none found, return blocker `no_valid_work_window`.
 *     Otherwise return the clamped start.
 */
export function clampFlexibleStartToNextWorkWindow(
  input: ClampFlexibleStartInput,
): ClampFlexibleStartResult {
  const blockers: AfterHoursBlocker[] = [];
  const proposedMs = parseTs(input.proposedStart);
  if (proposedMs == null) {
    blockers.push('invalid_time_input');
    return { shouldClamp: false, clampedStart: null, blockers };
  }
  if (input.isFlexible === false) {
    return { shouldClamp: false, clampedStart: null, blockers };
  }
  if (input.afterHoursConfirmed === true) {
    blockers.push('confirmed_after_hours');
    return { shouldClamp: false, clampedStart: null, blockers };
  }

  // Is the proposed start after-hours for the relevant window?
  const shiftEndMs = parseTs(input.shiftEnd);
  const shopCloseMs = parseTs(input.shopClose);
  // If we have neither shift end nor shop close, we cannot reason
  // about after-hours. Surface the gap as a blocker but do not clamp.
  if (shiftEndMs == null && shopCloseMs == null) {
    blockers.push('missing_shift_window');
    blockers.push('missing_shop_hours');
    return { shouldClamp: false, clampedStart: null, blockers };
  }
  // After-hours = proposedStart >= the EARLIER of shift end / shop close
  // (whichever is more restrictive).
  const restrictiveEnd =
    shiftEndMs != null && shopCloseMs != null
      ? Math.min(shiftEndMs, shopCloseMs)
      : (shiftEndMs ?? shopCloseMs)!;
  if (proposedMs < restrictiveEnd) {
    return { shouldClamp: false, clampedStart: null, blockers };
  }

  // Proposed is after-hours and ticket is flexible + not confirmed →
  // clamp.
  blockers.push('downstream_clamp_required');
  const next = getNextValidWorkWindowStart({
    now: new Date(proposedMs),
    shopHoursByDay: input.shopHoursByDay,
  });
  if (!next) {
    blockers.push('no_valid_work_window');
    return { shouldClamp: false, clampedStart: null, blockers };
  }
  return { shouldClamp: true, clampedStart: next, blockers };
}

/**
 * Boolean wrapper for downstream cascade callers: should this
 * proposed-start be clamped? Sister to `clampFlexibleStartToNextWorkWindow`
 * for code paths that only need the boolean.
 */
export function shouldClampDownstreamAfterHours(
  input: ClampFlexibleStartInput,
): boolean {
  return clampFlexibleStartToNextWorkWindow(input).shouldClamp;
}

/**
 * True iff a flexible ticket may be scheduled into the after-hours
 * window for the supplied context. Returns true ONLY when
 * `afterHoursConfirmed === true`.
 */
export function canScheduleAfterHours(input: ClampFlexibleStartInput): boolean {
  return input.afterHoursConfirmed === true;
}

// =============================================================================
// Adoption points (documented in code; no behavior change in this slice)
// =============================================================================
//
// 1. Display banner (smallest next step):
//    OptiBoard / TechnicianColumn renders an amber banner when
//    `getAfterHoursWorkState({ now, shiftEnd, isTimerRunning })`
//    returns 'after_hours_unconfirmed' / 'after_hours_grace'. The
//    banner offers two buttons: "Confirm working after hours" (sets
//    `afterHoursConfirmed`) and "Stop timer" (routes to the existing
//    timer-stop UI — NOT auto-applied).
//
// 2. Manager / writer escalation surface:
//    When state === 'after_hours_escalated', send a Pensieve / OCIS
//    notification with `manager_warning_required` blocker. Phase 6.5+
//    can persist `after_hours_confirmation_events` (id, ticket_id,
//    tech_id, confirmed_at, confirmed_by) so audit captures every
//    confirmation moment.
//
// 3. Phase 1.2 availability integration:
//    `getTechnicianAvailabilityState` already returns `after_hours_active`.
//    Extend its `AvailabilityContext` with the same confirmation field;
//    when unconfirmed past grace, return `after_hours_escalated` as a
//    new sub-state (or keep `after_hours_active` and surface the
//    sub-state only on the Phase 6.4 verdict). The cleaner path is the
//    latter — Phase 1.2 retains its existing 12 states; Phase 6.4 is
//    the confirmation overlay.
//
// 4. Cascade clamp adoption:
//    `globalCascade.ts` / `localCascade.ts` place flexible tickets into
//    next-available slots. Add a pre-write call to
//    `clampFlexibleStartToNextWorkWindow({ proposedStart, now,
//    shiftEnd, shopClose, shopHoursByDay, isFlexible: true })`. When
//    `shouldClamp === true`, use `clampedStart`. This is a tightly-
//    scoped narrowing — it never expands scheduling surface, only
//    prevents accidental after-hours placement.
//
// 5. Shop Culture surface for grace minutes:
//    Add `after_hours_grace_min` to `shop_culture` (defaulting to 15).
//    Route through `AfterHoursContext.graceMin`. No DB migration
//    needed — `shop_culture` accepts arbitrary JSON columns.
//
// 6. Audit reservation:
//    Reserve `AFTER_HOURS_CONFIRMED` / `AFTER_HOURS_ESCALATED` in
//    `AdminActionType` (TypeScript-only, same Phase 5.3 pattern). No
//    writer yet; Phase 6.5+ wires it.
//
// 7. Telemetry:
//    Log `verdict.state` distribution per board scan. Production data
//    drives whether the default grace of 15 minutes is right and
//    whether escalation fires often enough to warrant a manager
//    workflow.
